import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

const BUCKET_LABEL = {
  over100: '>100% Match',
  p100: '100% Match',
  p76_99: '76-99% Match',
  p51_75: '51-75% Match',
  p25_50: '25-50% Match',
  p0_24: '0-24% Match',
  noData: 'No Stated Count',
};

const pctCell = (b) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: 'var(--muted)', marginLeft: 4 }}>({b?.pct || 0}%)</span>
  </>
);

export default function HouseholdRoster({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, bucket: 'all', records: [], loading: false });
  const [recordPopup, setRecordPopup] = useState({ open: false, record: null });
  const [membersPopup, setMembersPopup] = useState({ open: false, record: null, members: [], loading: false });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/roster/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching roster data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalHouseholds = summary?.totalHouseholds ?? rows.reduce((s, r) => s + (Number(r.totalHouseholds) || 0), 0);
  const fullMatch = summary?.p100?.count ?? rows.reduce((s, r) => s + (Number(r.p100?.count) || 0), 0);
  const overMatch = summary?.over100?.count ?? 0;
  const partialLow = (summary?.p76_99?.count ?? 0) + (summary?.p51_75?.count ?? 0) + (summary?.p25_50?.count ?? 0);
  const worst = summary?.p0_24?.count ?? 0;

  const tiles = (
    <>
      <StatTile label="Total Households" value={totalHouseholds} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile label="100% Match" value={fullMatch} icon={CheckCircleIcon} accent="var(--success)" />
      <StatTile label=">100% Match" value={overMatch} icon={WarningAmberIcon} accent="var(--info)" sub="More members found than stated" />
      <StatTile label="Partial Mismatch" value={partialLow} icon={WarningAmberIcon} accent="var(--warning)" sub="76-99% / 51-75% / 25-50%" />
      <StatTile label="0-24% Match" value={worst} icon={ErrorIcon} accent="var(--danger)" />
    </>
  );

  const openDrilldown = async (row, bucket = 'all') => {
    setDrawer({ open: true, row, bucket, records: [], loading: true });
    try {
      const params = buildParams(applied, {
        extra: { groupBy: activeTab, groupValue: row.name, bucket, limit: 100 },
      });
      const res = await axios.get(`${API_URL}/roster/details?${params}`);
      const records = res.data?.data || [];
      setDrawer({ open: true, row, bucket, records, loading: false });
    } catch (err) {
      console.error('Error fetching roster detail records:', err);
      setDrawer({ open: true, row, bucket, records: [], loading: false });
    }
  };

  const bucketCell = (row, key) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, key); }}>
      {pctCell(row[key])}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalHouseholds', label: 'Total', align: 'center', render: (r) => <span className="cell-strong">{r.totalHouseholds || 0}</span> },
    { key: 'over100', label: '>100% Match', align: 'center', render: (r) => bucketCell(r, 'over100') },
    { key: 'p100', label: '100% Match', align: 'center', render: (r) => bucketCell(r, 'p100') },
    { key: 'p76_99', label: '76-99% Match', align: 'center', render: (r) => bucketCell(r, 'p76_99') },
    { key: 'p51_75', label: '51-75% Match', align: 'center', render: (r) => bucketCell(r, 'p51_75') },
    { key: 'p25_50', label: '25-50% Match', align: 'center', render: (r) => bucketCell(r, 'p25_50') },
    { key: 'p0_24', label: '0-24% Match', align: 'center', render: (r) => bucketCell(r, 'p0_24') },
  ];

  const openMembersPopup = async (record) => {
    setMembersPopup({ open: true, record, members: [], loading: true });
    try {
      const res = await axios.get(`${API_URL}/roster/members?uuid=${encodeURIComponent(record.uuid)}`);
      setMembersPopup({ open: true, record, members: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching household members:', err);
      setMembersPopup({ open: true, record, members: [], loading: false });
    }
  };

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    {
      key: 'household_id', label: 'Household ID',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openMembersPopup(r); }}>
          {r.household_id || '—'}
        </span>
      ),
    },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'statedTotal', label: 'Household Member', align: 'center', render: (r) => r.statedTotal ?? '—' },
    { key: 'actualCount', label: 'Members Found', align: 'center', render: (r) => r.actualCount ?? '—' },
    {
      key: 'matchPct', label: 'Match %', align: 'center',
      render: (r) => {
        if (r.matchPct === null || r.matchPct === undefined) return '—';
        const cls = r.matchPct === 100 ? 'badge-success' : r.matchPct >= 51 ? 'badge-warning' : 'badge-danger';
        return <span className={`badge ${cls}`}>{r.matchPct}%</span>;
      },
    },
    { key: 'block', label: 'Block', render: (r) => r.block || '—' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End Time', render: (r) => formatDate(r.end) },
    { key: 'duration', label: 'Duration', align: 'center', render: (r) => calcDuration(r.start, r.end) },
  ];

  const filterBar = (
    <FilterBar
      onApply={(v) => { setApplied(v); setPage(1); }}
      onClear={() => { setApplied(EMPTY); setPage(1); }}
      loading={loading}
    />
  );

  return (
    <RuleScreen
      title={rule?.title || 'Total Household Member Count vs Roster'}
      subtitle="Compares the stated total household members against the actual number of members recorded in the roster."
      icon={GroupsIcon}
      backTo={rule ? `/category/household` : undefined}
      backLabel="Back to Household"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filterBar}
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Household Count &amp; Members by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          onRowClick={(r) => openDrilldown(r, 'all')}
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${BUCKET_LABEL[drawer.bucket] || 'All Households'}` : ''}
        sections={
          drawer.row
            ? [
                {
                  title: 'Match Breakdown',
                  items: [
                    { label: 'Total Households', value: drawer.row.totalHouseholds || 0 },
                    { label: '>100% Match', value: drawer.row.over100?.count || 0 },
                    { label: '100% Match', value: drawer.row.p100?.count || 0 },
                    { label: '76-99% Match', value: drawer.row.p76_99?.count || 0 },
                    { label: '51-75% Match', value: drawer.row.p51_75?.count || 0 },
                    { label: '25-50% Match', value: drawer.row.p25_50?.count || 0 },
                    { label: '0-24% Match', value: drawer.row.p0_24?.count || 0 },
                  ],
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '-0.4rem' }}>
            Click a row to see that household's full detail.
          </p>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No households in this bucket."
            onRowClick={(r) => setRecordPopup({ open: true, record: r })}
            getRowKey={(r, i) => r.uuid || i}
          />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={recordPopup.open}
        onClose={() => setRecordPopup((p) => ({ ...p, open: false }))}
        title={recordPopup.record ? `Household: ${recordPopup.record.household_id}` : ''}
        sections={
          recordPopup.record
            ? [
                {
                  title: 'Household',
                  items: [
                    { label: 'Household ID', value: recordPopup.record.household_id },
                    { label: 'Respondent', value: recordPopup.record.respondent_name },
                    { label: 'Enumerator', value: recordPopup.record.enumerator_id },
                    { label: 'Block', value: recordPopup.record.block },
                    { label: 'Gram Panchayat', value: recordPopup.record.gram_panchayat },
                    { label: 'Village', value: recordPopup.record.village_name },
                  ],
                },
                {
                  title: 'Roster Match',
                  items: [
                    { label: 'Household Member', value: recordPopup.record.statedTotal },
                    { label: 'Members Found', value: recordPopup.record.actualCount },
                    { label: 'Match %', value: recordPopup.record.matchPct === null || recordPopup.record.matchPct === undefined ? '—' : `${recordPopup.record.matchPct}%` },
                    { label: 'Bucket', value: BUCKET_LABEL[recordPopup.record.bucket] || recordPopup.record.bucket },
                  ],
                },
                {
                  title: 'Survey Timing',
                  items: [
                    { label: 'Start Time', value: formatDate(recordPopup.record.start) },
                    { label: 'End Time', value: formatDate(recordPopup.record.end) },
                    { label: 'Duration', value: calcDuration(recordPopup.record.start, recordPopup.record.end) },
                  ],
                },
              ]
            : []
        }
      />

      <DetailDrawer
        open={membersPopup.open}
        onClose={() => setMembersPopup((p) => ({ ...p, open: false }))}
        title={membersPopup.record ? `Found Members: ${membersPopup.record.household_id}` : ''}
        sections={
          membersPopup.record
            ? [{ title: 'Household', items: [
                { label: 'Respondent', value: membersPopup.record.respondent_name },
                { label: 'Household Member (stated)', value: membersPopup.record.statedTotal },
                { label: 'Members Found', value: membersPopup.record.actualCount },
              ] }]
            : []
        }
      >
        <div className="detail-section">
          <h4>People Found ({membersPopup.members.length})</h4>
          <DataTable
            columns={[
              { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
              { key: 'name', label: 'Name', render: (m) => <span className="cell-strong">{m.name || '—'}</span> },
              { key: 'relation', label: 'Relation', render: (m) => m.relation || '—' },
              { key: 'gender', label: 'Gender', render: (m) => m.gender || '—' },
              { key: 'age', label: 'Age', align: 'center', render: (m) => m.age ?? '—' },
            ]}
            rows={membersPopup.members}
            loading={membersPopup.loading}
            emptyMessage="No members found for this household."
            getRowKey={(m, i) => `${m.name}-${i}`}
          />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
